import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '@cuspar/ui';
import { useDomainStore } from '../../state/domainStore';
import { useEffect, useState } from 'react';
import { useCartStore } from '../../state/cartStore';
import { IPurchaseDomainType, ICommonAddItemToCartRequest, ICommonDomainCheckResponse, ProductCategory, IPrice } from '@cuspar/ywt.ch.model';
import { wait } from '../../utils';
import { ROUTES } from '../../routes';
import { DomainSearchForm } from '../../components/Forms/DomainSearchForm';
import { PriceGadget } from '../../components/UI/PriceGadget';
import { Check, X } from 'lucide-react';

export const RegisterDomainPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isSearching, setIsSearching] = useState(false);
  const [addingToCartDomain, setAddingToCartDomain] = useState('');
  const [checkedDomains, setCheckedDomains] = useState<ICommonDomainCheckResponse[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCartStore((state) => state.cart);

  function onDomainSearch({ domain }: { domain: string }) {
    navigate(`${ROUTES.domains.search}?search=${domain}`);
  }

  const search = searchParams.get('search') || '';

  useEffect(() => {
    // check domain availability on page load completion
    setTimeout(() => {
      onPageLoad();
    }, 100);
  }, [search, location.key]);

  async function onPageLoad() {
    if (!search) return;

    setIsSearching(true);
    await wait();
    const domains = await checkDomainAvailability(search);
    setCheckedDomains(domains);
    setIsSearching(false);
  }

  const prices = useCartStore((state) => state.prices);
  const checkDomainAvailability = useDomainStore((state) => state.checkDomainAvailability);
  const addItemToCart = useCartStore((state) => state.addItemToCart);

  async function addDomainToCart(domain: ICommonDomainCheckResponse) {
    setAddingToCartDomain(domain.domain);
    const domainPrice = getDomainPrice(domain, prices, IPurchaseDomainType.Registration);
    const domainCartItem: ICommonAddItemToCartRequest = {
      type: 'domain',
      item: {
        domain: domain.domain,
        type: IPurchaseDomainType.Registration,
        price: domainPrice?.price ?? 0,
      },
    };
    await addItemToCart(domainCartItem);
    setAddingToCartDomain('');
  }

  function isDomainInCart(domain: ICommonDomainCheckResponse) {
    if (!cart) return false;
    return cart.domains.some((item) => item.domain === domain.domain);
  }

  return (
    <>
      <h1 className='font-mono text-center text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-2'>{t('domain.searchTitle')}</h1>
      <p className='text-center text-sm text-muted-foreground mb-10'>{t('domain.searchDescription')}</p>
      <DomainSearchForm className='container max-w-3xl mx-auto' defaultDomain={search} onSubmit={onDomainSearch}>
        <Button loading={isSearching} size='sm'>
          {t('common.search')}
        </Button>
      </DomainSearchForm>
      <div className='container mt-10 space-y-3 w-full max-w-3xl mx-auto'>
        {isSearching ? (
          <>
            <Skeleton className='w-full h-[40px] rounded-full mb-2' />
            <Skeleton className='w-full h-[40px] rounded-full mb-2' />
          </>
        ) : (
          checkedDomains.map((domain, i) => {
            const registrationPrice = getDomainPrice(domain, prices, IPurchaseDomainType.Registration);
            const renewalPrice = getDomainPrice(domain, prices, IPurchaseDomainType.Renewal);

            return (
              <div key={i} className='grid grid-cols-[1fr_auto_auto] py-2 px-4 rounded-xs border bg-card text-card-foreground shadow-sm w-full gap-5'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='font-medium leading-none flex gap-2 items-center'>
                      {domain.available ? (
                        <>
                          <Check className='text-green-600 h-4 w-4' />
                          <span className='inline-block max-w-64 break-all text-wrap'>{domain.domain}</span>
                        </>
                      ) : (
                        <>
                          <X className='text-destructive h-4 w-4' />
                          <span className=' text-gray-400 line-through'>{domain.domain}</span>
                        </>
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent align='start'>
                    <p>{domain.domain}</p>
                  </TooltipContent>
                </Tooltip>

                <div className='text-xs text-muted-foreground flex flex-col items-end'>
                  {domain.available && registrationPrice && renewalPrice && (
                    <>
                      <p>
                        <PriceGadget price={registrationPrice.price} currency={registrationPrice.currency} /> {t('domain.perYear')}
                      </p>
                      <p>
                        {t('domain.renewsAt')} <PriceGadget price={renewalPrice.price} currency={renewalPrice.currency} />
                      </p>
                    </>
                  )}
                </div>
                <div className='flex justify-end items-center col-span-2 sm:col-span-1'>
                  {domain.available ? (
                    isDomainInCart(domain) ? (
                      <span className='text-sm text-green-600'>{t('common.inCart')}</span>
                    ) : (
                      <Button loading={addingToCartDomain === domain.domain} onClick={() => addDomainToCart(domain)} variant='outline' size='sm' className='h-6 border-dashed'>
                        {t('domain.addToCart')}
                      </Button>
                    )
                  ) : (
                    <span className='text-sm text-secondary-foreground'>{domain.reason}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Below-the-fold info + registration FAQ (prerendered for SEO via forceMount) */}
      <section className='container max-w-3xl mx-auto px-4 mt-28 pb-8'>
        <h2 className='font-mono text-lg font-medium text-center mb-2 text-muted-foreground'>{t('domain.searchInfoTitle')}</h2>
        <p className='text-center text-sm text-muted-foreground mb-8 max-w-2xl mx-auto'>{t('domain.searchInfoIntro')}</p>
        <Accordion type='single' collapsible className='w-full grid md:grid-cols-2 gap-x-8'>
          <AccordionItem value='reg-eligibility'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRegEligibilityQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqRegEligibilityA')}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='reg-speed'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRegSpeedQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqRegSpeedA')}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='reg-dnssec'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRegDnssecQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqRegDnssecA')}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='reg-transfer-in'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('domain.faqRegTransferInQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('domain.faqRegTransferInA')}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </>
  );
};

export function getDomainPrice(domain: ICommonDomainCheckResponse, prices: IPrice[], type: IPurchaseDomainType) {
  return prices.find((p) => p.product === ProductCategory.domain && domain.domain.endsWith(p.tld ?? '') && p.type === type);
}
